import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  InsurancePolicyDocumentModel,
  InsurancePolicyQueryParams,
  InsurancePolicyUploadBatchResponse,
} from "@/features/cle-compliance/models/insurancePolicy";
import {
  mapInsurancePoliciesPageFromApi,
  mapInsurancePolicyFromApi,
} from "@/features/cle-compliance/utils/mapInsurancePolicy";
import { del, get, getBlob, postForm } from "./util";

export const INSURANCE_POLICIES_QUERY_KEY = ["insurance-policies"] as const;

export const INSURANCE_POLICIES_LIST_QUERY_KEY = [
  ...INSURANCE_POLICIES_QUERY_KEY,
  "list",
] as const;

export const createInsurancePolicyDetailQueryKey = (id: string) =>
  [...INSURANCE_POLICIES_QUERY_KEY, "detail", id] as const;

export const createInsurancePolicyFileQueryKey = (id: string) =>
  [...INSURANCE_POLICIES_QUERY_KEY, "files", id] as const;

const INSURANCE_POLICIES_QUERY = "documents/insurance-policies";

const createInsurancePolicyDownloadQuery = (id: string): string =>
  `${INSURANCE_POLICIES_QUERY}/${id}/download`;

const createSearchParams = ({
  page,
  page_size,
}: InsurancePolicyQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: InsurancePolicyQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString
    ? `${INSURANCE_POLICIES_QUERY}?${queryString}`
    : INSURANCE_POLICIES_QUERY;
};

export const fetchInsurancePolicies = (
  params: InsurancePolicyQueryParams = {},
): Promise<PaginatedResponse<InsurancePolicyDocumentModel>> =>
  get<PaginatedResponse<Record<string, unknown>>>({
    query: createListQuery(params),
  }).then(mapInsurancePoliciesPageFromApi);

export const fetchInsurancePolicyById = (
  id: string,
): Promise<InsurancePolicyDocumentModel> =>
  get<Record<string, unknown>>({
    query: `${INSURANCE_POLICIES_QUERY}/${id}`,
  }).then(mapInsurancePolicyFromApi);

export const deleteInsurancePolicy = async (id: string): Promise<void> => {
  await del({
    query: `${INSURANCE_POLICIES_QUERY}/${id}`,
  });
};

export const uploadInsurancePoliciesBatch = (
  files: File[],
): Promise<InsurancePolicyUploadBatchResponse> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("files", file);
  });

  return postForm<InsurancePolicyUploadBatchResponse>({
    query: `${INSURANCE_POLICIES_QUERY}/upload-batch`,
    body: formData,
  });
};

export const fetchInsurancePolicyDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createInsurancePolicyDownloadQuery(id),
  });
